import {
  type FileResponse,
  type GraphResponse,
  type IraiNodeResponse,
  IraiService,
  type NodeResponse,
} from '@/services/IraiService';
import { type PayloadAction, createSlice } from '@reduxjs/toolkit';
import type { RootEpic } from '../types';
import { filter, switchMap, map, catchError, forkJoin, of, concat } from 'rxjs';
import { GraphService } from '@/services/GraphService';

interface AnnotationHistoryItem {
  fromPage: number;
  toPage: number;
  annotationId?: string;
}

interface IraiState {
  _id?: string;
  project_id?: string;
  name_irai?: string;
  selectedFile?: FileResponse;
  selectedGraph?: GraphResponse;
  selectedIraiNode?: IraiNodeResponse;
  selectedIraiNodeId?: string;
  selectedTitleBlock?: string;
  isIraiNode: boolean;
  selectedGraphId?: string;
  fromDateTime?: string;
  toDateTime?: string;
  iraiVerify?: boolean;
  files: FileResponse[];
  graphs: GraphResponse[];

  annotationHistory?: AnnotationHistoryItem[];
}

const initialState: IraiState = {
  isIraiNode: true,
  files: [],
  graphs: [],
  annotationHistory: [] as AnnotationHistoryItem[],
};

const iraiSlice = createSlice({
  name: 'irai',
  initialState,
  reducers: {
    setIrai: (state, action: PayloadAction<IraiState>) => {
      state._id = action.payload._id;
      state.project_id = action.payload.project_id;
      state.name_irai = action.payload.name_irai;
    },
    setIraiId: (state, action: PayloadAction<string>) => {
      if (state._id !== action.payload) {
        state.selectedFile = undefined;
        state.selectedGraph = undefined;
      }
      state._id = action.payload;
    },
    setSelectedFile: (
      state,
      action: PayloadAction<FileResponse | undefined>
    ) => {
      state.selectedFile = action.payload;
    },
    setSelectedFileId: (state, action: PayloadAction<string>) => {
      if (state.selectedFile) {
        state.selectedFile = {
          ...state.selectedFile,
          _id: action.payload,
        };
      }
    },
    setSelectedGraph: (
      state,
      action: PayloadAction<GraphResponse | undefined>
    ) => {
      console.log("selected graph",action.payload);
      state.selectedGraph = action.payload;
    },
    setSelectedIraiNode: (
      state,
      action: PayloadAction<IraiNodeResponse | undefined>
    ) => {
      state.selectedIraiNode = action.payload;
    },
    setSelectedGraphId: (state, action: PayloadAction<string>) => {
      state.selectedGraphId = action.payload;
    },
    setFromDateTime: (state, action: PayloadAction<string>) => {
      state.fromDateTime = action.payload;
    },
    setToDateTime: (state, action: PayloadAction<string>) => {
      state.toDateTime = action.payload;
    },
    setIraiVerify: (state, action: PayloadAction<boolean>) => {
      state.iraiVerify = action.payload;
    },
    setIsIraiNode: (state, action: PayloadAction<boolean>) => {
      state.isIraiNode = action.payload;
    },
    setSelectedIraiNodeId: (state, action: PayloadAction<string>) => {
      state.selectedIraiNodeId = action.payload;
    },
    setSelectedTitleBlock: (state, action: PayloadAction<string>) => {
      state.selectedTitleBlock = action.payload;
    },
    setSelectedGraphPage: (state, action: PayloadAction<number>) => {
      const newPage = action.payload;
      const foundGraph = state.graphs.find(g => g.page === newPage);

      if (foundGraph) {
        state.selectedGraph = foundGraph;
      } else if (state.selectedGraph) {
        state.selectedGraph.page = newPage;
      }
    },

    fetchFilesAndGraphsRequest: (state, action: PayloadAction<string>) => { },
    setFiles: (state, action: PayloadAction<FileResponse[]>) => {
      state.files = action.payload;
    },
    setGraphs: (state, action: PayloadAction<GraphResponse[]>) => {
      state.graphs = action.payload;
    },
     pushPageHistory: (state, action: PayloadAction<number>) => {
      // Xóa page cũ nếu đã có
      state.annotationHistory = (state.annotationHistory ?? []).filter(
        item => item.toPage !== action.payload
      );
      // Thêm page mới
      state.annotationHistory.push({ fromPage: action.payload, toPage: action.payload });
    },
    pushAnnotationHistory: (
      state,
      action: PayloadAction<{ fromPage: number; toPage: number; annotationId?: string }>
    ) => {
      if (!state.annotationHistory) {
        state.annotationHistory = [];
      }
      state.annotationHistory.push(action.payload);
    },
    clearPageHistory: (state) => {
      state.annotationHistory = [];
    },
  }
});


export const fetchFilesAndGraphs$: RootEpic = (action$) =>
  action$.pipe(
    filter(iraiActions.fetchFilesAndGraphsRequest.match),
    switchMap((action) =>
      IraiService.Get.getFilesByIraiId(action.payload).pipe(
        switchMap((files) =>
          GraphService.Get.getAllGraphs(action.payload).pipe(
            switchMap((graphs) =>
              concat(
                of(iraiActions.setFiles(files)),
                of(iraiActions.setGraphs(graphs))
              )
            )
          )
        ),
        catchError((err) =>
          of(iraiActions.setFiles([]), iraiActions.setGraphs([]))
        )
      )
    )
  );



export const iraiEpics = [
  fetchFilesAndGraphs$
];
export const iraiActions = iraiSlice.actions;
export const iraiReducer = iraiSlice.reducer;
